import { useState } from 'react';
import { useReadContract } from 'wagmi';
import { parseUnits, type Address } from 'viem';
import { ADAPTER_ABI, BSC_EID, EMPTY_BYTES } from '../constants';
import { padAddressToBytes32 } from '../utils/formatters';

interface UseQuoteFeesParams {
  adapterAddress: Address | undefined;
  userAddress: Address | undefined;
  sourceChainId: number;
  amount: string;
  decimals: number;
}

/**
 * Hook for quoting bridge fees
 * Calls the adapter's quoteSend function to get nativeFee and lzTokenFee
 */
export const useQuoteFees = ({
  adapterAddress,
  userAddress,
  sourceChainId,
  amount,
  decimals,
}: UseQuoteFeesParams) => {
  // State to control when to fetch fees (only when user clicks button)
  const [shouldFetch, setShouldFetch] = useState(false);

  // Parse amount and prepare parameters
  const amountBigInt = amount && parseFloat(amount) > 0 ? parseUnits(amount, decimals) : 0n;
  const paddedAddress = userAddress ? padAddressToBytes32(userAddress) : ('0x' as `0x${string}`);

  // Build SendParam struct
  // Note: Using EMPTY_BYTES for extraOptions (gas limit will be set by LayerZero defaults)
  const sendParam = {
    dstEid: BSC_EID,
    to: paddedAddress,
    amountLD: amountBigInt,
    minAmountLD: amountBigInt,
    extraOptions: EMPTY_BYTES, // Use empty bytes - LayerZero will use default gas settings
    composeMsg: EMPTY_BYTES,
    oftCmd: EMPTY_BYTES,
  };

  // Quote fees from adapter contract
  const {
    data: feeData,
    isLoading: isFetchingFee,
    isSuccess: isFeeQuoted,
    error: feeError,
  } = useReadContract({
    address: adapterAddress,
    abi: ADAPTER_ABI,
    functionName: 'quoteSend',
    args: [sendParam, false], // false = don't pay in LZ token
    chainId: sourceChainId,
    query: {
      // Only fetch when user clicks button AND all params are valid
      enabled:
        shouldFetch &&
        !!adapterAddress &&
        !!userAddress &&
        amountBigInt > 0n &&
        !!sourceChainId,
    },
  });

  // Extract fees from response
  const nativeFee = feeData?.nativeFee ?? 0n;
  const lzTokenFee = feeData?.lzTokenFee ?? 0n;

  /**
   * Trigger fee quote
   * Sets shouldFetch to true, which enables the query and triggers the fetch
   */
  const quoteFees = () => {
    setShouldFetch(true);
  };

  /**
   * Reset quote state (called when amount or chain changes)
   * Disables the query so user must click button again
   */
  const resetQuote = () => {
    setShouldFetch(false);
  };

  return {
    nativeFee,
    lzTokenFee,
    isFetchingFee,
    isFeeQuoted,
    quoteFees,
    resetQuote,
    feeError,
  };
};
